import type { SerializedStyles } from '@emotion/react';

import gql from 'graphql-tag';
import { compose } from 'recompose';

import idvVendorGql from 'js/lib/idvVendorGql';
import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import type { ButtonProps } from '@coursera/cds-core';

import idVerificationMeQuery from 'bundles/verification/api/IdVerificationMeQuery.graphql';
import type { IdVerificationMeQuery } from 'bundles/verification/api/__generated__/IdVerificationMeQuery';
import type { IdVerificationVendorStatus_IdVerificationVendorIntegration_latestByMe as VerificationStatus } from 'bundles/verification/api/__generated__/IdVerificationVendorStatus';

export type UserVerificationData = {
  firstName: string;
  middleName: string;
  lastName: string;
  verifiedBy: string;
  jumioIdVerificationAttemptHistory: {
    failedAttemptCount: number;
  };
};

type InputProps = {
  showCTAonRight?: boolean;
  openInNewTab?: boolean;
  buttonVariant?: ButtonProps['variant'];
  buttonSize?: ButtonProps['size'];
  edgeAlign?: ButtonProps['edgeAlign'];
  hideVerificationMessage?: boolean;
  customStyles?: SerializedStyles;
  verifyIdButtonTextOverride?: string;
  verifyNameButtonTextOverride?: string;
  onVerificationComplete?: () => void;
};

export type Props = InputProps & {
  additionalQueryParams: { [key: string]: string | boolean };
  userVerificationInformation: UserVerificationData;
  verificationRequirements: string;
  latestVerificationStatus: VerificationStatus;
  refetchVerificationStatus: () => void;
};

export default compose<Props, InputProps>(
  waitForGraphQL(
    gql`
      query GetUserVerificationRequirements($ids: [String!]!) {
        UserVerificationRequirementsV1Resource {
          multiGet(ids: $ids) {
            elements {
              id
              userVerificationRequirementState
            }
          }
        }
      }
    `,
    {
      props: ({
        data: {
          // @ts-expect-error TSMIGRATION
          UserVerificationRequirementsV1Resource,
        },
      }) => ({
        verificationRequirements:
          UserVerificationRequirementsV1Resource?.multiGet?.elements?.[0]?.userVerificationRequirementState,
      }),
      options: () => {
        return {
          variables: {
            ids: [user.get().id.toString()],
          },
        };
      },
    }
  ),
  waitForGraphQL(idVerificationMeQuery, {
    // Guard against a failed/absent GetIdVerificationHistory response: if the query errors
    // (e.g. a transient gateway/Assembler outage), `data.IdVerification.me` is
    // undefined. Optional chaining + the NONE fallback degrades gracefully instead of
    // throwing during SSR and 500-ing the whole account-settings page.
    props: ({ data }: { data?: IdVerificationMeQuery }) => ({
      userVerificationInformation: data?.IdVerification?.me || {
        verifiedBy: 'NONE',
      },
    }),
  }),
  waitForGraphQL(
    idvVendorGql`
      query GetIdVerificationVendorStatus {
        IdVerificationVendorIntegration {
          latestByMe {
              verificationId
              verificationStatus
          }
        }
      }
`,
    {
      options: { context: { clientName: 'gatewayGql' } },
      props: ({ data }: $TSFixMe) => {
        return {
          latestVerificationStatus: data?.IdVerificationVendorIntegration?.latestByMe,
          refetchVerificationStatus: data.refetch,
        };
      },
    }
  )
);
